import { FC, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AddIcon from "@mui/icons-material/Add";
import { useTransactionTickets } from "../../../../../../../stores/Transactions/transaction.queries";
import CreateTicketForm from "../../../../../../Ticketing/TicketEditCreate/createTicketForm";
import TicketDialog from "../../../../../../Ticketing/TicketDialog";
import TicketsTable from "../../../../../../Ticketing/TicketsTable";
import { TicketDto } from "../../../../../../../api-client";

interface TicketsPanelProps {
  transactionId: string;
}

export const TicketsPanel: FC<TicketsPanelProps> = ({ transactionId }) => {
  const {
    data: tickets,
    isLoading,
    refetch,
  } = useTransactionTickets(transactionId);
  const [openCreateTicket, setOpenCreateTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TicketDto | null>(null);
  const [openTicketDialog, setOpenTicketDialog] = useState(false);

  const handleOpenTicketForm = () => {
    setOpenCreateTicket(true);
  };

  const handleCloseTicketForm = () => {
    setOpenCreateTicket(false);
    refetch();
  };

  const handleRowClick = (ticket: TicketDto) => {
    setSelectedTicket(ticket);
    setOpenTicketDialog(true);
  };

  const handleCloseTicketDialog = () => {
    setOpenTicketDialog(false);
    setSelectedTicket(null);
    refetch();
  };

  return (
    <>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          borderRadius: 2,
          bgcolor: "#D9DFE2",
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 1,
            bgcolor: "white",
            color: "black",
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <ConfirmationNumberIcon fontSize="small" sx={{ color: "#757575" }} />
          <Typography variant="body2" fontWeight="500">
            Tickets
          </Typography>
          {tickets && tickets.length > 0 && (
            <Chip
              label={tickets.length}
              size="small"
              sx={{
                bgcolor: "#003059",
                color: "white",
                height: 20,
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Neues Ticket erstellen">
            <IconButton
              size="small"
              onClick={handleOpenTicketForm}
              sx={{
                color: "#003059",
                width: 12,
                height: 12,
                padding: 0,
                "&:hover": {
                  bgcolor: "rgba(0, 48, 89, 0.1)",
                },
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            bgcolor: "#D9DFE2",
          }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              p={2}
            >
              <CircularProgress size={24} />
            </Box>
          ) : !tickets || tickets.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <ConfirmationNumberIcon
                sx={{ fontSize: 40, opacity: 0.3, mb: 1 }}
              />
              <Typography variant="caption" align="center">
                Keine Tickets
              </Typography>
            </Box>
          ) : (
            <TicketsTable
              handleRowClick={handleRowClick}
              ticketsData={tickets}
              hasMoreData={false}
              fetchMoreData={() => {}}
              isLoading={false}
              scrollId="tickets-panel-scroll"
            />
          )}
        </Box>
      </Paper>

      {/* Render dialogs in a separate portal-like container */}
      <Box sx={{ position: "fixed", top: 0, left: 0, pointerEvents: "none" }}>
        {openCreateTicket && (
          <Box sx={{ pointerEvents: "auto" }}>
            <CreateTicketForm
              openTicket={openCreateTicket}
              onCloseTicketForm={handleCloseTicketForm}
              data={{
                transactionId: transactionId,
              }}
            />
          </Box>
        )}

        {openTicketDialog && selectedTicket && (
          <Box sx={{ pointerEvents: "auto" }}>
            <TicketDialog
              open={openTicketDialog}
              handleClose={handleCloseTicketDialog}
              ticketId={selectedTicket.id}
            />
          </Box>
        )}
      </Box>
    </>
  );
};
